import PropTypes from "prop-types";
import React, { useCallback, useContext, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { useDispatch } from "react-redux";
import { LoadingContext, M_StateActions, MTable } from "src/M_Components";

import "../config-page.css";

//region Constants

/**
 * Route used to open the Contact CRUD page in a tab.
 */
const CONTACT_CRUD_ROUTE = "/home/contacts/contactConfigCrud";

//endregion

//region Component

/**
 * Renders the Contacts configuration page.
 *
 * Responsibilities:
 * - render the common configuration-page layout;
 * - keep the page visually aligned with other Config pages through config-page.css;
 * - render the Contacts MTable;
 * - use the Accounts permission module so existing permissions continue to apply;
 * - replace default MTable CRUD actions with tab-based navigation.
 *
 * @param {object} props - Component props.
 * @param {Array<object>} props.roles - User role permissions.
 * @param {Function} props.onCreateTab - Opens a new application tab.
 * @param {Function} props.toastMessages - Shows toast notifications.
 * @returns {JSX.Element} Contacts configuration page.
 */
export default function ContactConfig({ roles, onCreateTab, toastMessages }) {
    const ctx = useContext(LoadingContext);
    const dispatch = useDispatch();
    const { t } = useTranslation();

    const [refresh, setRefresh] = useState(false);

    /**
     * Removes table actions that are not required in this configuration page.
     *
     * Important:
     * - Print is not part of the desired Contacts workflow;
     * - Copy is disabled to keep contact creation explicit.
     */
    const removeContextItems = useMemo(() => ["Print", "Copy"], []);

    /**
     * Opens the Contact CRUD tab for the requested operation.
     *
     * Flow:
     * - persists the operation in M_State;
     * - validates row selection for View/Edit/Copy;
     * - persists the selected contact row when required;
     * - opens the configured CRUD route with a contextual tab label.
     *
     * @param {"Create"|"Edit"|"Copy"|"View"} operation - Requested CRUD operation.
     * @param {object|null|undefined} row - Selected contact row.
     * @returns {void}
     */
    const openContactCrud = useCallback(
        (operation, row) => {
            dispatch(
                M_StateActions.addM_State({
                    key: "operation",
                    value: operation,
                })
            );

            if (["Edit", "Copy", "View"].includes(operation)) {
                if (!row) {
                    toastMessages("error", "", t("SelectedLine"));
                    return;
                }

                dispatch(
                    M_StateActions.addM_State({
                        key: "ContactLine",
                        value: row,
                    })
                );
            }

            const safeName = row?.Name ?? "";

            const tabLabel =
                operation === "Create"
                    ? t("LabelNewContact")
                    : operation === "View"
                        ? `${t("LabelViewContact")} "${safeName}"`
                        : `${t("LabelEditContact")} "${safeName}"`;

            onCreateTab(CONTACT_CRUD_ROUTE, tabLabel);
        },
        [dispatch, onCreateTab, t, toastMessages]
    );

    /**
     * Replaces MTable default CRUD handlers with the tab-based Contact workflow.
     */
    const replaceFunctions = useMemo(
        () =>
            new Map([
                ["View", (row) => openContactCrud("View", row)],
                ["Create", () => openContactCrud("Create")],
                ["Edit", (row) => openContactCrud("Edit", row)],
            ]),
        [openContactCrud]
    );

    return (
        <div
            className="config-page"
            style={{ "--config-page-title-color": ctx.secondaryColor }}
        >
            <div className="config-page__header">
                <div className="config-page__title">
                    {t("Contacts")}
                </div>

                <div className="config-page__description">
                    {t("InfoContacts")}
                </div>
            </div>

            <div className="config-page__table">
                <MTable
                    filtersOnTable
                    entityName="Contacts"
                    roleName="Accounts"
                    rolePermissions={roles?.map((role) => role.Value)}
                    removeContextItems={removeContextItems}
                    refresh={refresh}
                    setRefresh={setRefresh}
                    useSessionStorage={false}
                    replaceFunctions={replaceFunctions}
                />
            </div>
        </div>
    );
}

//endregion

//region PropTypes

ContactConfig.propTypes = {
    roles: PropTypes.array,
    onCreateTab: PropTypes.func,
    toastMessages: PropTypes.func,
};

//endregion